import { Card, Typography } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"

import { type RootState } from "../../../../../../store.ts"
import { serializeData } from "../../../../../../utils/utils"
import { selectProjectWritePermissions } from "../../../../helpers/project-permissions.selectors.ts"
import {
  fetchProjectCommissions,
  patchProjectCommission
} from "../../../../redux/project-commissions/project-commissions.thunks.ts"
import type { ProjectCommission } from "../../../../redux/project-commissions/project-commissions.types.ts"
import { fetchFinancialSnapshot } from "../../../../redux/project-finance/project-finance.thunks.ts"
import { projectSetProp } from "../../../../redux/project.slice.ts"
import { type ProjectCommissionsFilters } from "../../../../redux/project.types.ts"
import { ProjectViewCommissionsList } from "./ProjectViewCommissions/ProjectViewCommissionsList.tsx"

const ProjectViewCommissionsSection = ({
  commissionsData,
  filters,
  commissionsPermissions,
  setProp,
  getProjectCommissions,
  updateProjectCommission,
  refetchFinancialSnapshot
}: ConnectedProps<typeof connector>) => {
  const projectId = useParams()?.id as string
  const commissions = commissionsData?.rows || []

  const [fetching, setFetching] = useState(false)
  const [editableRow, setEditableRow] = useState<string>("")
  const [ordering, setOrdering] = useState<string | null>(null)

  const setFilters = useCallback(
    (data: Partial<ProjectCommissionsFilters>) => {
      setProp({
        key: "commissionsItemsFilters",
        value: { ...filters, ...data }
      })
    },
    [setProp, filters]
  )

  const handleRefetchFinancialSnapshot = useCallback(() => {
    refetchFinancialSnapshot(projectId)
  }, [refetchFinancialSnapshot, projectId])

  const handleChangePage = useCallback(
    (page: number) => {
      setFilters({ page })
    },
    [setFilters]
  )

  const handleGetCommissions = useCallback(async () => {
    try {
      const serializeFilters = serializeData(
        filters || {}
      ) as ProjectCommissionsFilters

      setFetching(true)
      await getProjectCommissions({
        projectId,
        ordering,
        ...serializeFilters
      })?.unwrap()
    } finally {
      setFetching(false)
    }
  }, [getProjectCommissions, filters, ordering, projectId])

  const handleUpdateCommission = useCallback(
    async (data: ProjectCommission) => {
      try {
        await updateProjectCommission({
          projectId,
          data: { ...data, id: editableRow }
        })
        handleRefetchFinancialSnapshot()
        return setEditableRow("")
      } catch (e) {
        console.log("Update commission item error: ", e)
      }
    },
    [
      editableRow,
      projectId,
      updateProjectCommission,
      handleRefetchFinancialSnapshot
    ]
  )

  const onSelectRow = useCallback(
    (record: ProjectCommission, action: string) => {
      switch (action) {
        case "edit_row":
          return setEditableRow(record?.id)
        case "update":
          return handleUpdateCommission(record)
        case "cancel":
          return setEditableRow("")
      }
    },
    [setEditableRow, handleUpdateCommission]
  )

  useEffect(() => {
    void handleGetCommissions()
  }, [handleGetCommissions])

  return (
    <Card
      className="overflow-hidden"
      classNames={{ body: "p-0", header: "p-2" }}
      title={<Typography.Title level={4}>Commissions</Typography.Title>}
    >
      <ProjectViewCommissionsList
        fetching={fetching}
        commissions={commissions}
        summary={commissionsData?.summary}
        page={filters?.page}
        pageSize={filters?.page_size}
        count={commissions?.length || 0}
        editableRow={editableRow}
        options={{}}
        editAllowed={commissionsPermissions?.project_commission_edit}
        onOrderingChange={setOrdering}
        onSelectRow={onSelectRow}
        onPageChange={handleChangePage}
      />
    </Card>
  )
}

const mapStateToProps = (state: RootState) => ({
  commissionsData: state.project.commissions,
  filters: state.project.commissionsItemsFilters,
  commissionsPermissions: selectProjectWritePermissions(state)?.commissions
})

const mapDispatchToProps = {
  getProjectCommissions: fetchProjectCommissions,
  updateProjectCommission: patchProjectCommission,
  setProp: projectSetProp,
  refetchFinancialSnapshot: fetchFinancialSnapshot
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewCommissionsSection)
